import { COOKIE_CONSENT_KEY } from "./localStorageKeys";

export interface CookieConsentSettings {
  essential: true;
  analytics: boolean;
  marketing: boolean;
  version: number;
  updatedAt: string;
}

export const CONSENT_VERSION = 1;

export const GA_MEASUREMENT_ID = "G-DH9XPVCTK2";

export const OPEN_COOKIE_PREFERENCES_EVENT = "open-cookie-preferences";
export const COOKIE_CONSENT_CHANGED_EVENT = "cookie-consent-changed";

export const DEFAULT_SETTINGS: CookieConsentSettings = {
  essential: true,
  analytics: false,
  marketing: false,
  version: CONSENT_VERSION,
  updatedAt: "",
};

export const getStoredConsent = (): CookieConsentSettings | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CookieConsentSettings>;
    if (!parsed || parsed.version !== CONSENT_VERSION) return null;
    return {
      essential: true,
      analytics: !!parsed.analytics,
      marketing: !!parsed.marketing,
      version: CONSENT_VERSION,
      updatedAt: parsed.updatedAt ?? "",
    };
  } catch {
    return null;
  }
};

export const saveConsent = (
  settings: Omit<CookieConsentSettings, "essential" | "version" | "updatedAt">
): CookieConsentSettings => {
  const toStore: CookieConsentSettings = {
    essential: true,
    analytics: !!settings.analytics,
    marketing: !!settings.marketing,
    version: CONSENT_VERSION,
    updatedAt: new Date().toISOString(),
  };
  try {
    window.localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(toStore));
  } catch {
    // errors
  }
  applyConsent(toStore);
  window.dispatchEvent(
    new CustomEvent<CookieConsentSettings>(COOKIE_CONSENT_CHANGED_EVENT, {
      detail: toStore,
    })
  );
  return toStore;
};

export const applyConsent = (settings: CookieConsentSettings) => {
  if (settings.analytics) {
    loadGoogleAnalytics();
  } else {
    removeGoogleAnalytics();
  }
};

let gaLoadingPromise: Promise<void> | null = null;

const loadGoogleAnalytics = () => {
  if (typeof window === "undefined") return;
  if (window.__ga_loaded__) return;
  if (gaLoadingPromise) return;

  const w = window as unknown as {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    __ga_loaded__?: boolean;
  };

  w.dataLayer = w.dataLayer || [];
  w.gtag = function gtag(...args: unknown[]) {
    w.dataLayer.push(args);
  };
  w.gtag("js", new Date());
  w.gtag("config", GA_MEASUREMENT_ID, { anonymize_ip: true });

  gaLoadingPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.setAttribute("data-cookie-consent", "analytics");
    script.onload = () => {
      w.__ga_loaded__ = true;
      resolve();
    };
    script.onerror = () => reject(new Error("Failed to load Google Analytics"));
    document.head.appendChild(script);
  });
};

const removeGoogleAnalytics = () => {
  if (typeof document === "undefined") return;
  document
    .querySelectorAll('script[data-cookie-consent="analytics"]')
    .forEach((node) => node.remove());

  const gaCookiePrefixes = ["_ga", "_gid", "_gat"];
  const hostname = window.location.hostname;
  const domainParts = hostname.split(".");
  const rootDomain =
    domainParts.length > 1
      ? `.${domainParts.slice(-2).join(".")}`
      : hostname;

  document.cookie.split(";").forEach((cookie) => {
    const name = cookie.split("=")[0].trim();
    if (gaCookiePrefixes.some((prefix) => name.startsWith(prefix))) {
      document.cookie = `${name}=; Max-Age=0; path=/;`;
      document.cookie = `${name}=; Max-Age=0; path=/; domain=${hostname};`;
      document.cookie = `${name}=; Max-Age=0; path=/; domain=${rootDomain};`;
    }
  });

  const w = window as unknown as { __ga_loaded__?: boolean };
  w.__ga_loaded__ = false;
  gaLoadingPromise = null;
};

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    __ga_loaded__?: boolean;
  }
}

export const openCookiePreferences = () => {
  window.dispatchEvent(new CustomEvent(OPEN_COOKIE_PREFERENCES_EVENT));
};

export const PRIVACY_POLICY_URL =
  "https://mobile.trackmategps.com/html/privacy-policy.html";
